import { useState, useEffect } from "react";
import { useApi } from "@/lib/api";
import { toast } from "sonner";
import { 
  FileText, 
  Download, 
  Eye, 
  Calendar,
  Building2,
  Plus,
  FileDown
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { downloadReportPDF } from "@/utils/pdfGenerator";

export default function Reports() {
  const api = useApi();
  const [platforms, setPlatforms] = useState([]);
  const [reports, setReports] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [includeRecommendations, setIncludeRecommendations] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [previewReport, setPreviewReport] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [platformsRes, reportsRes] = await Promise.all([
        api.get("/api/platforms"),
        api.get("/api/reports")
      ]);
      setPlatforms(platformsRes.data);
      setReports(reportsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setInitialLoading(false);
    }
  };

  const handlePlatformToggle = (platformId) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformId) 
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  const generateReport = async () => {
    if (selectedPlatforms.length === 0) {
      toast.error("Please select at least one platform");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/api/reports/generate", {
        platform_ids: selectedPlatforms,
        include_recommendations: includeRecommendations
      });
      setPreviewReport(response.data);
      setShowPreview(true);
      setReports(prev => [response.data, ...prev]);
      toast.success("Report generated successfully");
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownloadPDF = (report) => {
    try {
      const filename = downloadReportPDF(report);
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  if (initialLoading) {
    return (
      <div className="content-wrapper flex items-center justify-center min-h-[50vh]">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="content-wrapper" data-testid="reports-page">
      <div className="page-header">
        <h1 className="page-title">Reports</h1>
        <p className="page-subtitle">
          Generate detailed compliance reports with actionable recommendations
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Report Generator */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white border border-zinc-200 rounded-sm p-6">
            <h3 className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-4">
              Generate New Report
            </h3>
            
            <div className="mb-4">
              <Label className="text-sm font-medium text-zinc-700 mb-2 block">
                Select Platforms
              </Label>
              <div className="space-y-2">
                {platforms.map((platform) => (
                  <div 
                    key={platform.id}
                    className={`p-3 border rounded-sm transition-colors cursor-pointer ${
                      selectedPlatforms.includes(platform.id) 
                        ? 'border-violet-300 bg-violet-50' 
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                    onClick={() => handlePlatformToggle(platform.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox 
                        checked={selectedPlatforms.includes(platform.id)}
                        onCheckedChange={() => handlePlatformToggle(platform.id)}
                        data-testid={`platform-${platform.id}`}
                      />
                      <Label className="font-medium text-zinc-900 cursor-pointer">
                        {platform.name}
                      </Label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <div 
                className={`p-3 border rounded-sm transition-colors cursor-pointer ${
                  includeRecommendations 
                    ? 'border-violet-300 bg-violet-50' 
                    : 'border-zinc-200'
                }`}
                onClick={() => setIncludeRecommendations(!includeRecommendations)}
              >
                <div className="flex items-center gap-3">
                  <Checkbox 
                    checked={includeRecommendations}
                    onCheckedChange={setIncludeRecommendations}
                    data-testid="include-recommendations"
                  />
                  <div>
                    <Label className="font-medium text-zinc-900 cursor-pointer">
                      Include Recommendations
                    </Label>
                    <p className="text-xs text-zinc-500">Add prioritized action items</p>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={generateReport}
              disabled={loading || selectedPlatforms.length === 0}
              className="btn-primary w-full justify-center"
              data-testid="generate-report-btn"
            >
              {loading ? (
                <>
                  <div className="loading-spinner w-4 h-4"></div>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Plus size={18} />
                  <span>Generate Report</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Reports List */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-zinc-200 rounded-sm">
            <div className="px-6 py-4 border-b border-zinc-200">
              <h3 className="text-sm font-semibold text-zinc-900">
                Generated Reports ({reports.length})
              </h3>
            </div>
            
            {reports.length === 0 ? (
              <div className="p-12 text-center">
                <FileText size={48} className="mx-auto mb-4 text-zinc-300" />
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">No Reports Yet</h3>
                <p className="text-sm text-zinc-500">
                  Generate your first compliance report using the form on the left
                </p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {reports.map((report) => (
                  <div 
                    key={report.id}
                    className="p-4 hover:bg-zinc-50 transition-colors"
                    data-testid={`report-${report.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText size={16} className="text-violet-600" />
                          <span className="font-medium text-zinc-900">
                            Compliance Analysis Report
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {report.platforms.map((platform) => (
                            <Badge key={platform} variant="secondary" className="text-xs">
                              {platform}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {formatDate(report.generated_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Building2 size={12} />
                            {report.findings?.length || 0} platforms analyzed
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setPreviewReport(report);
                          setShowPreview(true);
                        }}
                        className="btn-secondary py-2 px-3"
                        data-testid={`view-report-${report.id}`}
                      >
                        <Eye size={14} />
                        <span>View</span>
                      </button>
                      <button
                        onClick={() => handleDownloadPDF(report)}
                        className="btn-secondary py-2 px-3 ml-2"
                        data-testid={`download-report-${report.id}`}
                      >
                        <FileDown size={14} />
                        <span>PDF</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Compliance Analysis Report</span>
              {previewReport && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDownloadPDF(previewReport)}
                  className="ml-4"
                  data-testid="download-preview-pdf"
                >
                  <FileDown size={16} className="mr-2" />
                  Download PDF
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {previewReport && (
            <div className="report-preview" data-testid="report-preview">
              <div className="report-header">
                <h1>EPR/PPR Compliance Analysis</h1>
                <div className="report-meta">
                  <span>Generated: {formatDate(previewReport.generated_at)}</span>
                  <span>Platforms: {previewReport.platforms.join(', ')}</span>
                </div>
              </div>

              <div className="report-section">
                <h2>Executive Summary</h2>
                <p>{previewReport.executive_summary}</p>
              </div>

              <div className="report-section">
                <h2>Platform Findings</h2>
                <div className="space-y-4">
                  {previewReport.findings?.map((finding, idx) => (
                    <div key={idx} className="p-4 bg-zinc-50 rounded-sm">
                      <h3 className="font-semibold text-zinc-900 mb-2">{finding.platform}</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-zinc-500">Markets:</span>
                          <span className="ml-2 text-zinc-900">{finding.markets?.join(', ')}</span>
                        </div>
                        <div>
                          <span className="text-zinc-500">In-House Capabilities:</span>
                          <span className="ml-2 text-green-600 font-medium">{finding.in_house_capabilities}</span>
                        </div>
                        <div>
                          <span className="text-zinc-500">Gaps Identified:</span>
                          <span className="ml-2 text-violet-600 font-medium">{finding.gaps_identified}</span>
                        </div>
                        <div>
                          <span className="text-zinc-500">Full Outsource Opportunities:</span>
                          <span className="ml-2 text-violet-600 font-medium">{finding.full_outsource_opportunities}</span>
                        </div>
                      </div>
                      {finding.key_gaps?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-zinc-200">
                          <span className="text-xs font-medium text-zinc-500 uppercase">Key Gaps:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {finding.key_gaps.map(gap => (
                              <Badge key={gap} className="bg-violet-100 text-violet-700 text-xs">
                                {gap.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {previewReport.recommendations?.length > 0 && (
                <div className="report-section">
                  <h2>Recommendations</h2>
                  <div className="space-y-3">
                    {previewReport.recommendations.map((rec, idx) => (
                      <div key={idx} className="p-4 border-l-4 border-violet-500 bg-violet-50">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={
                            rec.priority === 'P0' ? 'bg-red-500' :
                            rec.priority === 'P1' ? 'bg-amber-500' : 'bg-blue-500'
                          }>
                            {rec.priority}
                          </Badge>
                          <span className="font-semibold text-zinc-900">{rec.title}</span>
                        </div>
                        <p className="text-sm text-zinc-600 mb-1">{rec.rationale}</p>
                        <span className="text-xs text-violet-600">Impact: {rec.estimated_impact}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {previewReport.service_matrix?.length > 0 && (
                <div className="report-section">
                  <h2>Service Opportunity Matrix</h2>
                  <div className="overflow-x-auto">
                    <table className="gap-table">
                      <thead>
                        <tr>
                          <th>Service</th>
                          <th>Amazon</th>
                          <th>Temu</th>
                          <th>GPAIS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewReport.service_matrix.map((row, idx) => (
                          <tr key={idx}>
                            <td className="font-medium">{row.service}</td>
                            <td>
                              <span className={`gap-badge ${
                                row.amazon.includes('Full') ? 'opportunity' :
                                row.amazon.includes('Partial') ? 'in-house' : ''
                              }`}>
                                {row.amazon}
                              </span>
                            </td>
                            <td>
                              <span className={`gap-badge ${
                                row.temu.includes('Full') ? 'opportunity' :
                                row.temu.includes('Partial') ? 'in-house' : ''
                              }`}>
                                {row.temu}
                              </span>
                            </td>
                            <td>
                              <span className={`gap-badge ${
                                row.gpais.includes('Full') ? 'opportunity' :
                                row.gpais.includes('Partial') ? 'in-house' : ''
                              }`}>
                                {row.gpais}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
